import React, { useState } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import "../../styles.css";

const MISReportContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [financialYear, setFinancialYear] = useState(() => {
    // Auto-select current financial year based on date
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const currentYear = today.getFullYear();

    if (currentMonth >= 4) {
      // April to December: FY is current year to next year
      const nextYear = currentYear + 1;
      return `${currentYear}-${String(nextYear).slice(-2)}`;
    } else {
      // January to March: FY is previous year to current year
      const previousYear = currentYear - 1;
      return `${previousYear}-${String(currentYear).slice(-2)}`;
    }
  });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [misData, setMisData] = useState({
    salesIncome: '',
    fdInterest: '',
    plywoodPurchases: '',
    rawMaterialPurchases: '',
    carpenter: '',
    electricalWork: ''
  });

  const financialYears = ["2024-25", "2025-26", "2026-27"];
  const months = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

  const [fyAnchor, setFyAnchor] = useState(null);

  const handleClearFinancialYear = (event) => {
    event.stopPropagation();
    setFinancialYear("");
    setFyAnchor(null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
  };

  // Get display format for month and year (e.g., "Aug'24" or "Jan'25")
  const getMonthYearDisplay = () => {
    if (!selectedMonth || !financialYear) return "";

    const [startYear, endYearShort] = financialYear.split('-');
    const endYear = `20${endYearShort}`;

    // Apr-Dec are in start year, Jan-Mar are in end year
    const monthsInStartYear = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const year = monthsInStartYear.includes(selectedMonth) ? startYear : endYear;

    return `${selectedMonth}'${year.slice(-2)}`;
  };

  return (
    <Box>
      {/* Financial Year and Month Selection Row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        {/* Financial Year — a real custom pill + menu, not a bordered MUI Select */}
        <Box
          onClick={(e) => setFyAnchor(e.currentTarget)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", md: "flex-start" },
            gap: 0.75,
            width: { xs: "100%", md: "180px" },
            background: fyAnchor ? "#eef6ff" : "#ffffff",
            border: `1px solid ${fyAnchor ? "#39aafc" : "#e4e7ec"}`,
            borderRadius: "12px",
            px: 1.75,
            py: "10px",
            fontSize: "13px",
            fontWeight: 700,
            color: financialYear ? "#1565C0" : "#5f6f86",
            fontFamily: "Roboto, Arial, sans-serif",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 0.18s ease",
            "&:hover": { borderColor: "#39aafc", background: "#eef6ff" },
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <CalendarMonthIcon sx={{ fontSize: 16, color: financialYear ? "#1565C0" : "#9aa7b8" }} />
            {financialYear ? `FY ${financialYear}` : "Financial Year"}
          </Box>
          {financialYear ? (
            <IconButton aria-label="clear" onClick={handleClearFinancialYear} size="small" sx={{ p: "2px", color: "#9aa7b8", "&:hover": { color: "#667085" } }}>
              <ClearIcon sx={{ fontSize: 15 }} />
            </IconButton>
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9aa7b8", transition: "transform 0.2s ease", transform: fyAnchor ? "rotate(180deg)" : "none" }} />
          )}
        </Box>
        <Menu
          anchorEl={fyAnchor}
          open={Boolean(fyAnchor)}
          onClose={() => setFyAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { borderRadius: "14px", mt: 0.5, minWidth: 180, boxShadow: "0 12px 32px rgba(16,24,40,0.16)", border: "1px solid #eef2f6" } }}
        >
          {financialYears.map((year) => {
            const selected = year === financialYear;
            return (
              <MenuItem
                key={year}
                selected={selected}
                onClick={() => { setFinancialYear(year); setFyAnchor(null); }}
                sx={{ fontSize: "13px", fontWeight: selected ? 700 : 500, color: selected ? "#1565C0" : "#344054", fontFamily: "Roboto, Arial, sans-serif", py: 1, "&.Mui-selected": { background: "#eef6ff" }, "&.Mui-selected:hover": { background: "#e2effe" } }}
              >
                <CheckIcon sx={{ fontSize: 16, mr: 1, visibility: selected ? "visible" : "hidden", color: "#39aafc" }} />
                FY {year}
              </MenuItem>
            );
          })}
        </Menu>

        {/* Month Selection Buttons */}
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {months.map((month) => (
            <Button
              key={month}
              variant={selectedMonth === month ? "contained" : "outlined"}
              onClick={() => handleMonthClick(month)}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: selectedMonth === month ? 700 : 600,
                fontFamily: "Roboto, Arial, sans-serif",
                minWidth: "65px",
                height: "38px",
                borderRadius: "11px",
                background: selectedMonth === month ? "linear-gradient(135deg, #4db2fc, #1565C0)" : "transparent",
                color: selectedMonth === month ? "#fff" : "#667085",
                borderColor: selectedMonth === month ? "transparent" : "#e4e7ec",
                boxShadow: selectedMonth === month ? "0 6px 16px rgba(21, 101, 192, 0.25)" : "none",
                "&:hover": {
                  background: selectedMonth === month ? "linear-gradient(135deg, #39aafc, #0d47a1)" : "rgba(57, 170, 252, 0.08)",
                  borderColor: selectedMonth === month ? "transparent" : "#39aafc",
                },
              }}
            >
              {month}
            </Button>
          ))}
        </Box>
      </Box>

      {/* MIS Report Content */}
      {selectedMonth && financialYear && (
        <Box sx={{ marginTop: "30px" }}>
          {/* Heading */}
          <Typography
            variant="h6"
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: "Roboto, Arial, sans-serif",
              color: "#333",
            }}
          >
            MIS Report for the Month {getMonthYearDisplay()}
          </Typography>

          {/* MIS Report Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
              borderRadius: "16px",
              border: "1px solid #eef2f6",
              overflow: "hidden",
              marginTop: "12px",
              width: { xs: "100%", md: "560px" }
            }}
          >
            <Table aria-label="mis report table">
              <TableHead>
                <TableRow>
                  <TableCell
                    className="table-head-cell"
                    sx={{ width: "25%", textAlign: "left", padding: "8px 10px", backgroundColor: "transparent" }}
                    rowSpan={2}
                  >
                    Particulars
                  </TableCell>
                  <TableCell
                    className="table-head-cell"
                    sx={{ width: "15%", textAlign: "center", padding: "8px 10px", backgroundColor: "transparent" }}
                    colSpan={2}
                  >
                    {getMonthYearDisplay()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    className="table-head-cell"
                    sx={{ width: "7.5%", textAlign: "left", padding: "8px 10px", backgroundColor: "transparent" }}
                  >
                    Amount in Rs.
                  </TableCell>
                  <TableCell
                    className="table-head-cell"
                    sx={{ width: "7.5%", textAlign: "left", padding: "8px 10px", backgroundColor: "transparent" }}
                  >
                    In %
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* 1. Revenue from Operations - Header Row (no input fields) */}
                <TableRow>
                  <TableCell
                    className="table-body-cell"
                    sx={{
                      fontWeight: 500,
                      textDecoration: "underline",
                      fontFamily: "Roboto, Arial, sans-serif",
                      borderBottom: "none",
                      color: "#059669"
                    }}
                  >
                    1.Revenue from Operations
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                </TableRow>

                {/* Sales Income - Editable Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "25px !important" }}>-Sales Income</TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", padding: "4px 10px" }}>
                    <TextField
                      type="text"
                      value={misData.salesIncome}
                      onChange={(e) => setMisData({ ...misData, salesIncome: e.target.value })}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                      }}
                      inputProps={{
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", textAlign: "left", padding: "0" }
                      }}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left" }}></TableCell>
                </TableRow>

                {/* FD Interest - Editable Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "25px !important" }}>-FD Interest</TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", padding: "4px 10px" }}>
                    <TextField
                      type="text"
                      value={misData.fdInterest}
                      onChange={(e) => setMisData({ ...misData, fdInterest: e.target.value })}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                      }}
                      inputProps={{
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", textAlign: "left", padding: "0" }
                      }}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left" }}></TableCell>
                </TableRow>

                {/* Total Revenue (A) Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ fontWeight: 500, fontFamily: "Roboto, Arial, sans-serif", paddingLeft: "25px !important", color: "#059669" }}>
                    Total Revenue (A)
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500, color: "#059669" }}>
                    {(() => {
                      const salesIncome = parseFloat(misData.salesIncome) || 0;
                      const fdInterest = parseFloat(misData.fdInterest) || 0;
                      const total = salesIncome + fdInterest;
                      return total.toFixed(2);
                    })()}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500, color: "#059669" }}>
                    {/* Percentage calculation will go here */}
                  </TableCell>
                </TableRow>

                {/* 2. Direct Expenditure - Header Row (no input fields, no border) */}
                <TableRow>
                  <TableCell
                    className="table-body-cell"
                    sx={{
                      fontWeight: 500,
                      textDecoration: "underline",
                      fontFamily: "Roboto, Arial, sans-serif",
                      borderBottom: "none",
                      color: "#dc2626"
                    }}
                  >
                    2. Direct Expenditure
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                </TableRow>

                {/* a) Material Purchases - Sub-header (no input fields, no border) */}
                <TableRow>
                  <TableCell
                    className="table-body-cell"
                    sx={{
                      fontWeight: 500,
                      textDecoration: "underline",
                      fontFamily: "Roboto, Arial, sans-serif",
                      paddingLeft: "25px !important",
                      borderBottom: "none"
                    }}
                  >
                    a) Material Purchases
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                </TableRow>

                {/* Plywood Purchases - Editable Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "25px !important" }}>-Plywood Purchases</TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", padding: "4px 10px" }}>
                    <TextField
                      type="text"
                      value={misData.plywoodPurchases}
                      onChange={(e) => setMisData({ ...misData, plywoodPurchases: e.target.value })}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                      }}
                      inputProps={{
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", textAlign: "left", padding: "0" }
                      }}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left" }}></TableCell>
                </TableRow>

                {/* Raw Material Purchases - Editable Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "25px !important" }}>-Raw Material Purchases</TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", padding: "4px 10px" }}>
                    <TextField
                      type="text"
                      value={misData.rawMaterialPurchases}
                      onChange={(e) => setMisData({ ...misData, rawMaterialPurchases: e.target.value })}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                      }}
                      inputProps={{
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", textAlign: "left", padding: "0" }
                      }}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left" }}></TableCell>
                </TableRow>

                {/* Total Material Purchases (B) Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ fontWeight: 500, fontFamily: "Roboto, Arial, sans-serif", paddingLeft: "25px !important" }}>
                    Total Material Purchases (B)
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500 }}>
                    {(() => {
                      const plywoodPurchases = parseFloat(misData.plywoodPurchases) || 0;
                      const rawMaterialPurchases = parseFloat(misData.rawMaterialPurchases) || 0;
                      const total = plywoodPurchases + rawMaterialPurchases;
                      return total.toFixed(2);
                    })()}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500 }}></TableCell>
                </TableRow>

                {/* b) Labour Costs - Sub-header (no input fields, no border) */}
                <TableRow>
                  <TableCell
                    className="table-body-cell"
                    sx={{
                      fontWeight: 500,
                      textDecoration: "underline",
                      fontFamily: "Roboto, Arial, sans-serif",
                      paddingLeft: "25px !important",
                      borderBottom: "none"
                    }}
                  >
                    b) Labour Costs
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", borderBottom: "none" }}></TableCell>
                </TableRow>

                {/* Carpenter - Editable Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "25px !important" }}>-Carpenter</TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", padding: "4px 10px" }}>
                    <TextField
                      type="text"
                      value={misData.carpenter}
                      onChange={(e) => setMisData({ ...misData, carpenter: e.target.value })}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                      }}
                      inputProps={{
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", textAlign: "left", padding: "0" }
                      }}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left" }}></TableCell>
                </TableRow>

                {/* Electrical Work - Editable Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ paddingLeft: "25px !important" }}>-Electrical Work</TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", padding: "4px 10px" }}>
                    <TextField
                      type="text"
                      value={misData.electricalWork}
                      onChange={(e) => setMisData({ ...misData, electricalWork: e.target.value })}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                      }}
                      inputProps={{
                        style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", textAlign: "left", padding: "0" }
                      }}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left" }}></TableCell>
                </TableRow>

                {/* Total Labour Cost (C) Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ fontWeight: 500, fontFamily: "Roboto, Arial, sans-serif", paddingLeft: "25px !important" }}>
                    Total Labour Cost (C)
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500 }}>
                    {(() => {
                      const carpenter = parseFloat(misData.carpenter) || 0;
                      const electricalWork = parseFloat(misData.electricalWork) || 0;
                      const total = carpenter + electricalWork;
                      return total.toFixed(2);
                    })()}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500 }}></TableCell>
                </TableRow>

                {/* Total Direct Expenditure D=(B+C) Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ fontWeight: 500, fontFamily: "Roboto, Arial, sans-serif", paddingLeft: "25px !important", color: "#dc2626" }}>
                    Total Direct Expenditure D=(B+C)
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500, color: "#dc2626" }}>
                    {(() => {
                      const plywoodPurchases = parseFloat(misData.plywoodPurchases) || 0;
                      const rawMaterialPurchases = parseFloat(misData.rawMaterialPurchases) || 0;
                      const carpenter = parseFloat(misData.carpenter) || 0;
                      const electricalWork = parseFloat(misData.electricalWork) || 0;
                      const totalMaterial = plywoodPurchases + rawMaterialPurchases;
                      const totalLabour = carpenter + electricalWork;
                      const totalExpenditure = totalMaterial + totalLabour;
                      return totalExpenditure.toFixed(2);
                    })()}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500, color: "#dc2626" }}></TableCell>
                </TableRow>

                {/* Gross Profit (A-D) Row */}
                <TableRow>
                  <TableCell className="table-body-cell" sx={{ fontWeight: 500, fontFamily: "Roboto, Arial, sans-serif", paddingLeft: "25px !important" }}>
                    Gross Profit (A-D)
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500 }}>
                    {(() => {
                      const salesIncome = parseFloat(misData.salesIncome) || 0;
                      const fdInterest = parseFloat(misData.fdInterest) || 0;
                      const totalRevenue = salesIncome + fdInterest;

                      const plywoodPurchases = parseFloat(misData.plywoodPurchases) || 0;
                      const rawMaterialPurchases = parseFloat(misData.rawMaterialPurchases) || 0;
                      const carpenter = parseFloat(misData.carpenter) || 0;
                      const electricalWork = parseFloat(misData.electricalWork) || 0;
                      const totalExpenditure = plywoodPurchases + rawMaterialPurchases + carpenter + electricalWork;

                      const grossProfit = totalRevenue - totalExpenditure;
                      return grossProfit.toFixed(2);
                    })()}
                  </TableCell>
                  <TableCell className="table-body-cell" sx={{ textAlign: "left", fontWeight: 500 }}>
                    {(() => {
                      const salesIncome = parseFloat(misData.salesIncome) || 0;
                      const fdInterest = parseFloat(misData.fdInterest) || 0;
                      const totalRevenue = salesIncome + fdInterest;

                      const plywoodPurchases = parseFloat(misData.plywoodPurchases) || 0;
                      const rawMaterialPurchases = parseFloat(misData.rawMaterialPurchases) || 0;
                      const carpenter = parseFloat(misData.carpenter) || 0;
                      const electricalWork = parseFloat(misData.electricalWork) || 0;
                      const totalExpenditure = plywoodPurchases + rawMaterialPurchases + carpenter + electricalWork;

                      const grossProfit = totalRevenue - totalExpenditure;
                      const percentage = totalRevenue !== 0 ? (grossProfit / totalRevenue) * 100 : 0;
                      return percentage.toFixed(2) + '%';
                    })()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default MISReportContent;
